import { Button, Dialog, Field, Notice } from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { api, errorMessage } from '../../lib/api';

interface Issued {
  accountId: string;
  publicId: number;
  link: string;
  expiresAt: string;
  emailed: boolean;
}

export const ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
];

/**
 * Account invitation (IAM-01 to IAM-03): name, email, role and plan, three independent
 * flags, and the single-use link shown back with one-click copy.
 */
export function InviteAccount({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<{ items: { id: string; name: string; customer: string }[] }>('/staff/directory/plans'),
    enabled: open,
  });
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'patient' as 'patient' | 'navigator',
    planId: '',
    phone: '',
    timezone: 'America/New_York',
    referralSource: '',
    isTest: false,
    isSupport: false,
    suppressEmail: false,
  });
  const [copied, setCopied] = useState(false);
  const issue = useMutation({
    mutationFn: () =>
      api<Issued>('/staff/accounts', {
        body: {
          ...form,
          planId: form.role === 'patient' ? form.planId || null : null,
          phone: form.phone || null,
          referralSource: form.referralSource || null,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['directory'] }),
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const close = (v: boolean) => {
    onOpenChange(v);
    if (!v) {
      issue.reset();
      setCopied(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    issue.mutate();
  };

  if (issue.data) {
    const d = issue.data;
    return (
      <Dialog
        open={open}
        onOpenChange={close}
        title="Invitation issued"
        description={`${form.firstName} ${form.lastName} is in the directory now as ID ${d.publicId}, not yet registered.`}
        footer={<Button onClick={() => close(false)}>Done</Button>}
      >
        <p style={{ margin: 0, fontSize: '.86rem', color: 'var(--ink-2)' }}>
          {d.emailed
            ? `An email with this link went to ${form.email}.`
            : 'No email was sent. Hand this link to the person another way.'}{' '}
          It works once and expires{' '}
          {new Date(d.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}.
        </p>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <input
            className="input mono"
            readOnly
            value={d.link}
            aria-label="Registration link"
            onFocus={(e) => e.target.select()}
          />
          <Button
            variant="sec"
            onClick={async () => {
              await navigator.clipboard.writeText(d.link);
              setCopied(true);
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title="Invite an account"
      description="Nobody registers themselves. The invitation binds the account to its role and plan."
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '.9rem' }} noValidate>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.9rem' }}>
          <Field label="First name">
            {(id) => (
              <input
                id={id}
                className="input"
                required
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
              />
            )}
          </Field>
          <Field label="Last name">
            {(id) => (
              <input
                id={id}
                className="input"
                required
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field
          label="Email address"
          hint={
            form.role === 'navigator'
              ? 'Staff accounts must use a cancerexpertnow.com or cancerexpert.now address.'
              : undefined
          }
        >
          {(id) => (
            <input
              id={id}
              className="input"
              type="email"
              required
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
            />
          )}
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.9rem' }}>
          <Field label="Role">
            {(id) => (
              <select
                id={id}
                className="select"
                value={form.role}
                onChange={(e) => set('role', e.target.value as 'patient' | 'navigator')}
              >
                <option value="patient">Patient</option>
                <option value="navigator">Care navigator</option>
              </select>
            )}
          </Field>
          {form.role === 'patient' ? (
            <Field label="Plan" hint="Sets what the patient is entitled to.">
              {(id, d) => (
                <select
                  id={id}
                  className="select"
                  required
                  value={form.planId}
                  aria-describedby={d}
                  onChange={(e) => set('planId', e.target.value)}
                >
                  <option value="">Choose a plan</option>
                  {plans.data?.items.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.customer} › {p.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ) : (
            <Field label="Time zone">
              {(id) => (
                <select
                  id={id}
                  className="select"
                  value={form.timezone}
                  onChange={(e) => set('timezone', e.target.value)}
                >
                  {ZONES.map((z) => (
                    <option key={z} value={z}>
                      {z.replace('America/', '').replace('_', ' ')}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}
        </div>
        {form.role === 'patient' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.9rem' }}>
            <Field label="Phone" hint="Optional. For inbound calls.">
              {(id, d) => (
                <input
                  id={id}
                  className="input"
                  value={form.phone}
                  aria-describedby={d}
                  onChange={(e) => set('phone', e.target.value)}
                />
              )}
            </Field>
            <Field label="Time zone">
              {(id) => (
                <select
                  id={id}
                  className="select"
                  value={form.timezone}
                  onChange={(e) => set('timezone', e.target.value)}
                >
                  {ZONES.map((z) => (
                    <option key={z} value={z}>
                      {z.replace('America/', '').replace('_', ' ')}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
        )}
        <Field label="Referral source" hint="Which channel produced this account (DIR-07).">
          {(id, d) => (
            <input
              id={id}
              className="input"
              value={form.referralSource}
              aria-describedby={d}
              onChange={(e) => set('referralSource', e.target.value)}
            />
          )}
        </Field>
        <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap' }}>
          <label className="check">
            <input type="checkbox" checked={form.isTest} onChange={(e) => set('isTest', e.target.checked)} />
            Test account
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={form.isSupport}
              onChange={(e) => set('isSupport', e.target.checked)}
            />
            Support account
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={form.suppressEmail}
              onChange={(e) => set('suppressEmail', e.target.checked)}
            />
            Do not email the link
          </label>
        </div>
        {issue.isError && <Notice tone="bad">{errorMessage(issue.error)}</Notice>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
          <Button variant="sec" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button type="submit" busy={issue.isPending}>
            Issue invitation
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
